import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import { EnvironmentalDataCard } from '@/components/organisms/EnvironmentalDataCard';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import type {
    OverviewData,
    RespirationData,
    SwimSpeedChartData,
    TiltChartData,
} from '@/domain/behavior';
import type { ApiError } from '@/domain/shared/errors';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';

import { SWIM_SPEED_THRESHOLD_DEFAULT, TILT_THRESHOLD_DEFAULT } from './BehaviorDashboardContainer';
import BehaviorOverviewMetrics from './components/BehaviorOverviewMetrics/BehaviorOverviewMetrics';
import BehaviorVideo from './components/BehaviorVideo/BehaviorVideo';
import RespirationOverviewChart from './components/RespirationOverviewChart/RespirationOverviewChart';
import RespirationPercentagesCard from './components/RespirationPercentagesCard/RespirationPercentagesCard';
import SwimSpeedOverviewChart from './components/SwimSpeedOverviewChart/SwimSpeedOverviewChart';
import TiltOverviewChart from './components/TiltOverviewChart/TiltOverviewChart';

import styles from './BehaviorDashboard.module.scss';

interface BehaviorDashboardViewProps {
    overview: OverviewData;
    overviewIsLoading: boolean;
    overviewError: ApiError | null;
    overviewRetry?: RetryProps;
    swimSpeedDateRange: DateRange;
    onSwimSpeedDateChange: (data: DateRange) => void;
    swimSpeedData: SwimSpeedChartData;
    swimSpeedIsLoading: boolean;
    swimSpeedError: ApiError | null;
    swimSpeedRetry?: RetryProps;
    respirationDateRange: DateRange;
    onRespirationDateChange: (data: DateRange) => void;
    respirationData: RespirationData;
    respirationIsLoading: boolean;
    respirationError: ApiError | null;
    respirationRetry?: RetryProps;
    tiltDateRange: DateRange;
    onTiltDateChange: (data: DateRange) => void;
    tiltData: TiltChartData;
    tiltIsLoading: boolean;
    tiltError: ApiError | null;
    tiltRetry?: RetryProps;
    cageName?: string;
    cageId: number;
    bioscopeId?: number;
}

export default function BehaviorDashboardView(props: BehaviorDashboardViewProps) {
    const {
        overview,
        overviewIsLoading,
        overviewError,
        overviewRetry,
        swimSpeedData,
        swimSpeedIsLoading,
        swimSpeedError,
        swimSpeedRetry,
        respirationData,
        respirationIsLoading,
        respirationError,
        respirationRetry,
        tiltData,
        tiltIsLoading,
        tiltError,
        tiltRetry,
        cageName,
        cageId,
        bioscopeId,
    } = props;

    return (
        <GridLayout contentLayout="1">
            <BehaviorOverviewMetrics
                overview={overview}
                isLoading={overviewIsLoading}
                error={overviewError}
                retry={overviewRetry}
            />
            <GridLayout contentLayout="3">
                {/* Left column */}
                <div className={styles.chartsColumn}>
                    <SwimSpeedOverviewChart
                        dateRange={props.swimSpeedDateRange}
                        onDateChange={props.onSwimSpeedDateChange}
                        swimSpeedDataPoints={swimSpeedData.data}
                        swimSpeedThreshold={SWIM_SPEED_THRESHOLD_DEFAULT}
                        isLoading={swimSpeedIsLoading}
                        error={swimSpeedError}
                        retry={swimSpeedRetry}
                    />
                    <RespirationOverviewChart
                        dateRange={props.respirationDateRange}
                        onDateChange={props.onRespirationDateChange}
                        respirationDataPoints={respirationData.data}
                        isLoading={respirationIsLoading}
                        error={respirationError}
                        retry={respirationRetry}
                    />
                    <TiltOverviewChart
                        dateRange={props.tiltDateRange}
                        onDateChange={props.onTiltDateChange}
                        tiltDataPoints={tiltData.data}
                        tiltThreshold={TILT_THRESHOLD_DEFAULT}
                        isLoading={tiltIsLoading}
                        error={tiltError}
                        retry={tiltRetry}
                    />
                </div>
                {/* Right column */}
                <div>
                    {overviewError ? (
                        <ContentBlock>
                            <SomethingHappened
                                onRetry={overviewRetry?.onRetry}
                                isRetrying={overviewRetry?.isRetrying}
                            />
                        </ContentBlock>
                    ) : (
                        <>
                            <ContentBlock>
                                <EnvironmentalDataCard
                                    isLoading={overviewIsLoading}
                                    oxygenConcentration={
                                        overview.environmentalFactors.oxygenConcentration
                                    }
                                    oxygenSaturation={
                                        overview.environmentalFactors.oxygenSaturation
                                    }
                                    temperature={overview.environmentalFactors.temperature}
                                    depth={overview.environmentalFactors.depth}
                                    salinity={overview.environmentalFactors.salinity}
                                    lastMeasurement={overview.environmentalFactors.lastMeasurement}
                                />
                            </ContentBlock>
                            <ContentBlock>
                                <RespirationPercentagesCard
                                    isLoading={overviewIsLoading}
                                    passive={overview.respirationPercentages.passive}
                                    active={overview.respirationPercentages.active}
                                    lastMeasurement={
                                        overview.respirationPercentages.lastMeasurement
                                    }
                                />
                            </ContentBlock>
                        </>
                    )}
                    <ContentBlock size="fit-content">
                        <BehaviorVideo
                            cageId={cageId}
                            bioscopeId={bioscopeId}
                            cageName={cageName}
                        />
                    </ContentBlock>
                </div>
            </GridLayout>
        </GridLayout>
    );
}
